// AiOrbitLoader — COMENIUS marketplace loading indicator.
//
// Vendored + modified from `loading-dev` (MIT License, Copyright (c) 2026 Jakub Krehel).
// https://github.com/jakubkrehel/loading — `src/orbit.tsx` + `src/easing.ts` +
// `src/motion.ts` + `src/frame.tsx` + `src/types.ts`, simplified to this single file.
// Modification: the center dot (`ld-orbit-dot`) is replaced with the COMENIUS
// 4-point AI star (inline SVG, inherits `currentColor`). MIT permission covers
// use / copy / modification / distribution provided this notice is kept.
//
// Defaults follow the COMENIUS dark theme: amber `#FAA31B` (`--color-primary`),
// 1400ms stacked rotation (approved in preview), `prefers-reduced-motion` safe.

import React from 'react';

export type AiOrbitEasing = 'linear' | 'ease-in-out' | 'stacked';

export interface AiOrbitLoaderProps {
  /** Diameter in px. Default 48. */
  size?: number;
  /** Any valid CSS color. Default COMENIUS amber. */
  color?: string;
  /** Rotation duration in ms (higher = slower). Default 1400. */
  duration?: number;
  /** Rotation easing. Default 'stacked'. */
  easing?: AiOrbitEasing;
  /** 'paused' | 'running'. Default 'running'. */
  playState?: 'paused' | 'running';
  /** Accessible label for the wrapping `role="status"`. */
  ariaLabel?: string;
  className?: string;
}

const CSS = `
.ld-orbit-ai {
  --ld-orbit-ai-stroke: calc(var(--ld-ai-size, 48px) * 0.0625);
  position: relative;
  width: var(--ld-ai-size, 48px);
  height: var(--ld-ai-size, 48px);
  flex-shrink: 0;
}
.ld-orbit-ai-track {
  position: absolute;
  inset: 8%;
  border-radius: 9999px;
  background: conic-gradient(from 180deg, transparent 0deg, currentColor 180deg, transparent 180deg);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - var(--ld-orbit-ai-stroke)), #000 0);
  mask: radial-gradient(farthest-side, transparent calc(100% - var(--ld-orbit-ai-stroke)), #000 0);
}
.ld-orbit-ai-spin {
  transform-origin: center;
  animation: ld-orbit-ai-rotate var(--ld-ai-duration, 1400ms) linear infinite;
  animation-play-state: var(--ld-ai-play-state, running);
}
.ld-orbit-ai-spin-ease-in-out {
  animation-timing-function: ease-in-out;
}
.ld-orbit-ai-spin-stacked {
  animation-name: ld-orbit-ai-rotate, ld-orbit-ai-rotate;
  animation-timing-function: linear, ease-in-out;
  animation-composition: add;
}
@keyframes ld-orbit-ai-rotate {
  to { transform: rotate(360deg); }
}
.ld-orbit-ai-star {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 34%;
  height: 34%;
  transform: translate(-50%, -50%);
}
@media (prefers-reduced-motion: reduce) {
  .ld-orbit-ai-spin {
    animation: none;
  }
}
`;

export function AiOrbitLoader({
  size = 48,
  color = '#FAA31B',
  duration = 1400,
  easing = 'stacked',
  playState = 'running',
  ariaLabel = 'Loading',
  className,
}: AiOrbitLoaderProps) {
  const trackClass =
    easing === 'linear'
      ? 'ld-orbit-ai-track ld-orbit-ai-spin'
      : `ld-orbit-ai-track ld-orbit-ai-spin ld-orbit-ai-spin-${easing}`;

  return (
    <>
      <style>{CSS}</style>
      <div
        role="status"
        aria-label={ariaLabel}
        className={['ld-orbit-ai', className].filter(Boolean).join(' ')}
        style={
          {
            '--ld-ai-size': `${size}px`,
            '--ld-ai-duration': `${duration}ms`,
            '--ld-ai-play-state': playState,
            color,
          } as React.CSSProperties
        }
      >
        <svg
          className="ld-orbit-ai-star"
          viewBox="0 0 100 100"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M50 0 C53 35 65 47 100 50 C65 53 53 65 50 100 C47 65 35 53 0 50 C35 47 47 35 50 0 Z" />
        </svg>
        <div className={trackClass} />
      </div>
    </>
  );
}

export interface AiLoadingStateProps {
  /** Loader diameter in px. Default 48. */
  size?: number;
  /** Uppercase label under the loader, e.g. "Starting Experience...". */
  label: string;
  /** Optional secondary line under the label. */
  sublabel?: string;
  /** Shows a "Try Again" button when true (slow-load fallback). */
  showRetry?: boolean;
  /** Retry button handler. */
  onRetry?: () => void;
  className?: string;
}

/**
 * Standard full-screen / card loading state for marketplace (dark theme):
 * centered AiOrbitLoader + uppercase label + optional retry.
 * Small inline button spinners intentionally keep the legacy CSS spinner.
 */
export function AiLoadingState({
  size = 48,
  label,
  sublabel,
  showRetry = false,
  onRetry,
  className,
}: AiLoadingStateProps) {
  return (
    <div
      className={['flex flex-col items-center justify-center text-center', className]
        .filter(Boolean)
        .join(' ')}
    >
      <AiOrbitLoader size={size} ariaLabel={label} />
      <p className="text-white/60 text-xs font-bold uppercase tracking-widest mt-4">
        {label}
      </p>
      {sublabel && (
        <p className="text-slate-500 text-xs mt-1">{sublabel}</p>
      )}
      {showRetry && onRetry && (
        <button
          onClick={onRetry}
          className="mt-4 px-5 h-11 rounded-xl border border-primary/40 text-primary text-xs font-bold uppercase tracking-widest hover:bg-primary/10 active:scale-95 transition-all"
        >
          Try Again
        </button>
      )}
    </div>
  );
}
